import { Bitmap, OUTLINE, shade, type RGB } from "./bitmap";

/** World units per floor tile. A tile is a 24×12 px diamond (2:1 isometric, as in the mocks). */
export const T = 6;

/** Isometric projection: P(x, y, z) in units -> pixels, relative to an origin. */
export function project(x: number, y: number, z: number, ox: number, oy: number): [number, number] {
  return [ox + (x - y) * 2, oy + (x + y) - z];
}

/**
 * Paints 3D boxes and quads into a bitmap (plus an emissive layer for things that glow).
 * Used for furniture sprites, wall segments and whole-floor bases.
 */
export class IsoPainter {
  readonly img: Bitmap;
  readonly em: Bitmap;
  constructor(
    w: number,
    h: number,
    public ox: number,
    public oy: number,
  ) {
    this.img = new Bitmap(w, h);
    this.em = new Bitmap(w, h);
  }

  P(x: number, y: number, z = 0): [number, number] {
    return project(x, y, z, this.ox, this.oy);
  }

  quad(pts: [number, number, number][], c: RGB, glow?: RGB): void {
    const q = pts.map(([x, y, z]) => this.P(x, y, z));
    this.img.poly(q, c);
    if (glow) this.em.poly(q, glow);
  }

  line(a: [number, number, number], b: [number, number, number], c: RGB): void {
    const [x0, y0] = this.P(...a);
    const [x1, y1] = this.P(...b);
    this.img.line(x0, y0, x1, y1, c);
  }

  /** A box from (x,y,z) of size w×d×h with lit top, left (y-facing) and right (x-facing) faces. */
  box(x: number, y: number, z: number, w: number, d: number, h: number, c: RGB, opts: { top?: RGB; outline?: boolean; glowLeft?: RGB } = {}): void {
    const top = opts.top ?? shade(c, 1.18);
    const right = shade(c, 0.74);
    if (h > 0) {
      this.quad([[x, y + d, z + h], [x + w, y + d, z + h], [x + w, y + d, z], [x, y + d, z]], c, opts.glowLeft);
      this.quad([[x + w, y, z + h], [x + w, y + d, z + h], [x + w, y + d, z], [x + w, y, z]], right);
    }
    this.quad([[x, y, z + h], [x + w, y, z + h], [x + w, y + d, z + h], [x, y + d, z + h]], top);
    if (h > 0) this.line([x, y + d, z + h], [x + w, y + d, z + h], shade(top, 1.15));
    if (opts.outline !== false) {
      const pts = ([[x, y, z + h], [x + w, y, z + h], [x + w, y, z], [x + w, y + d, z], [x, y + d, z], [x, y + d, z + h]] as [number, number, number][]).map(
        ([a, b, cc]) => this.P(a, b, cc),
      );
      for (let i = 0; i < pts.length; i++) {
        const [x0, y0] = pts[i];
        const [x1, y1] = pts[(i + 1) % pts.length];
        this.img.line(x0, y0, x1, y1, OUTLINE);
      }
    }
  }

  /** Flat rectangle on the floor (or at height z). */
  floor(x: number, y: number, w: number, d: number, c: RGB, z = 0.02): void {
    this.quad([[x, y, z], [x + w, y, z], [x + w, y + d, z], [x, y + d, z]], c);
  }

  /** A small upright panel facing the viewer along the y face (like a poster on a stand or a monitor). */
  panelY(x: number, y: number, z: number, w: number, h: number, c: RGB, glow?: RGB): void {
    this.quad([[x, y, z + h], [x + w, y, z + h], [x + w, y, z], [x, y, z]], c, glow);
  }

  /** A small upright panel along the x face. */
  panelX(x: number, y: number, z: number, d: number, h: number, c: RGB, glow?: RGB): void {
    this.quad([[x, y, z + h], [x, y + d, z + h], [x, y + d, z], [x, y, z]], c, glow);
  }

  /** Paste a flat bitmap onto a wall plane: "y" = the back wall (runs along x), "x" = the left wall. */
  decal(im: Bitmap, plane: "x" | "y", a: number, b: number, z: number, glow = false): void {
    const [sx, sy] = this.P(a, b, z).map(Math.round);
    for (let u = 0; u < im.w; u++) {
      const off = plane === "y" ? u >> 1 : -(u >> 1);
      for (let v = 0; v < im.h; v++) {
        const c = im.get(u, v);
        if (!c[3]) continue;
        this.img.set(sx + u, sy + v + off, [c[0], c[1], c[2]]);
        if (glow) this.em.set(sx + u, sy + v + off, [c[0], c[1], c[2]], 90);
      }
    }
  }
}

/** Footprint-sized painter for one sprite: the anchor (local 0,0,0) sits at (ox, oy). */
export function spritePainter(wTiles: number, hTiles: number, maxZ: number): IsoPainter {
  const w = (wTiles + hTiles) * T * 2 + 2;
  const h = (wTiles + hTiles) * T + maxZ + 2;
  return new IsoPainter(w, h, hTiles * T * 2 + 1, maxZ + 1);
}

export interface IsoSprite {
  img: Bitmap;
  em?: Bitmap;
  /** Pixel inside img that sits on the world anchor point (the footprint's back corner at floor level). */
  ax: number;
  ay: number;
  /** Footprint in tiles (after rotation). */
  w: number;
  h: number;
  /** Lights this sprite gives off, in local units relative to the anchor. */
  lights?: { x: number; y: number; z: number; color: RGB; radius: number; strength: number }[];
}
